/**
 * Mobile Vehicle Detail View
 * Shows vehicle summary, compliance, todos, and recent docs
 */

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  FileText,
  CheckSquare,
  AlertTriangle,
  Calendar,
  Gauge,
  Shield,
  Plus,
  ChevronRight,
  Camera,
} from 'lucide-react';
import { mobileAPI } from '../../api';
import DocumentCapture from '../components/DocumentCapture';
import { useFormat } from '../../i18n/format.js';

function getScoreColor(score) {
  if (score >= 80) return 'text-green-400';
  if (score >= 50) return 'text-yellow-400';
  return 'text-red-400';
}

function getScoreBg(score) {
  if (score >= 80) return 'bg-green-500/20';
  if (score >= 50) return 'bg-yellow-500/20';
  return 'bg-red-500/20';
}

export default function VehicleDetailView({ vehicleId, onBack }) {
  const { fmt } = useFormat();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCapture, setShowCapture] = useState(false);

  useEffect(() => {
    loadVehicle();
  }, [vehicleId]);

  const loadVehicle = async () => {
    setLoading(true);
    try {
      const result = await mobileAPI.getVehicleSummary(vehicleId);
      if (result.ok !== false) {
        setData(result);
      }
    } catch (err) {
      console.error('Failed to load vehicle:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!data?.vehicle) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <Gauge className="w-12 h-12 mb-2 opacity-50" />
        <p>Véhicule non trouvé</p>
        <button onClick={onBack} className="mt-4 text-blue-400">
          Retour
        </button>
      </div>
    );
  }

  const { vehicle, todos_preview = [], docs_preview = [], compliance } = data;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 safe-area-top">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 -ml-2 text-slate-400">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">{vehicle.license_plate}</h1>
            <p className="text-sm text-slate-400">
              {vehicle.make} {vehicle.model} {vehicle.year}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Vehicle Card */}
        <div className="bg-slate-800 rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-slate-700 rounded-lg flex items-center justify-center">
              <Gauge className="w-8 h-8 text-slate-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    vehicle.status === 'active'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}
                >
                  {vehicle.status === 'active' ? 'Actif' : vehicle.status}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <Gauge className="w-4 h-4" />
                  {vehicle.mileage?.toLocaleString()} km
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance Score */}
        <div className={`rounded-xl p-4 ${getScoreBg(vehicle.compliance_score || 100)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className={`w-8 h-8 ${getScoreColor(vehicle.compliance_score || 100)}`} />
              <div>
                <p className="text-sm text-slate-300">Score de conformité</p>
                <p className={`text-2xl font-bold ${getScoreColor(vehicle.compliance_score || 100)}`}>
                  {vehicle.compliance_score || 100}%
                </p>
              </div>
            </div>
            {vehicle.compliance_score < 80 && (
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            )}
          </div>
        </div>

        {/* Todos Preview */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-blue-400" />
              <h2 className="font-semibold text-white">Taches</h2>
              {todos_preview.length > 0 && (
                <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full">
                  {todos_preview.length}
                </span>
              )}
            </div>
          </div>

          {todos_preview.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              Aucune tache en cours
            </div>
          ) : (
            <ul className="divide-y divide-slate-700">
              {todos_preview.slice(0, 3).map((todo) => (
                <li key={todo.id} className="px-4 py-3 flex items-center gap-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      todo.status === 'overdue'
                        ? 'bg-red-500'
                        : todo.priority === 'high'
                        ? 'bg-amber-500'
                        : 'bg-blue-500'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{todo.title}</p>
                    {todo.due_date && (
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {fmt.date(todo.due_date)}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Documents Preview */}
        <div className="bg-slate-800 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-green-400" />
              <h2 className="font-semibold text-white">Documents</h2>
            </div>
            <button
              onClick={() => setShowCapture(true)}
              className="flex items-center gap-1 text-blue-400 text-sm bg-blue-500/10 px-3 py-1.5 rounded-lg active:bg-blue-500/20"
            >
              <Camera className="w-4 h-4" />
              Ajouter
            </button>
          </div>

          {docs_preview.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              Aucun document
            </div>
          ) : (
            <ul className="divide-y divide-slate-700">
              {docs_preview.slice(0, 3).map((doc) => (
                <li key={doc.id} className="px-4 py-3 flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">
                      {doc.title || doc.document_type}
                    </p>
                    <p className="text-xs text-slate-500">
                      {doc.review_status === 'pending'
                        ? 'En attente'
                        : doc.review_status === 'approved'
                        ? 'Approuve'
                        : doc.review_status}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Document Capture Modal */}
      {showCapture && (
        <DocumentCapture
          vehicleId={vehicleId}
          referenceId={`VHC_${String(vehicleId).padStart(6, '0')}`}
          scope="VEHICLE"
          onSuccess={() => {
            setShowCapture(false);
            loadVehicle(); // Reload to show new document
          }}
          onCancel={() => setShowCapture(false)}
        />
      )}
    </div>
  );
}
